import React from "react";

import { Advertisement } from "@/common/Outro/Advertisement";
import { InteractionOutro, OutroType } from "@/service/Model";

import { OutroOverlay } from "./index";
import { Outlink, Question, Registration } from "./Types";

/**
 * Handles renderinng of different outro types.
 * @param outro {object} Outro response
 * @param context
 * @param toggleOutro
 * @param onSubmitEnd
 */
export function renderOutro(
  outro: InteractionOutro,
  context: any,
  toggleOutro: (outroType: string) => void,
  onSubmitEnd?: (status?: string) => void
): JSX.Element | null {
  const { globals, locale } = context;
  let outroType: JSX.Element;
  const outroProps = {
    icons: globals.icons,
    onClose: toggleOutro.bind(this),
  };

  switch (outro.outroType) {
    case OutroType.Advertising:
      if (
        globals.tealiumConfig?.is_premium_user ||
        !outro.advertisingAdSlot ||
        !outro.advertisingWidth ||
        !outro.advertisingHeight
      ) {
        return null;
      }

      outroType = (
        <Advertisement
          adSlot={outro.advertisingAdSlot}
          height={Number(outro.advertisingHeight)}
          width={Number(outro.advertisingWidth)}
          isPreview={globals.preview}
        />
      );

      break;
    case OutroType.Outlink:
      outroType = (
        <Outlink
          outroType={OutroType.Outlink}
          showOnlyForCorrectAnswers={outro.showOnlyForCorrectAnswers}
          autoForwardTimerEnabled={outro.autoForwardTimerEnabled}
          autoForwardTimerSeconds={outro.autoForwardTimerSeconds}
          outlinkUrl={outro.outlinkUrl}
          iosOutlinkUrl={outro.iosOutlinkUrl}
          androidOutlinkUrl={outro.androidOutlinkUrl}
          background={outro.background}
          backgroundSource={outro.backgroundSource}
          backgroundCopyright={outro.backgroundCopyright}
          mobileBackground={outro.mobileBackground}
          mobileBackgroundSource={outro.mobileBackgroundSource}
          mobileBackgroundCopyright={outro.mobileBackgroundCopyright}
          noPadding={true}
          icons={globals.icons}
          mode="image"
          onClose={toggleOutro.bind(this, outro.outroType)}
        />
      );
      break;
    case OutroType.RegistrationForm:
      outroType = (
        <Registration
          outroType={OutroType.RegistrationForm}
          outroTitle={outro.outroTitle}
          termsAndConditionsLink={outro.termsAndConditionsLink}
          privacyPolicyLink={outro.privacyPolicyLink}
          advertisementOptIn={outro.advertisementOptIn}
          hansIdOptIn={outro.hansIdOptIn}
          onlyWithRegistration={outro.onlyWithRegistration}
          lightRegistration={outro.lightRegistration}
          customFields={outro.customFields}
          registrationStatus={globals.registrationStatus}
          alignedToTop={
            globals.hyperAvailable &&
            globals.response?.interaction?.type === "voting"
          }
          title={locale?.translation.registerAgain}
          icons={globals.icons}
          brand={globals.brand?.theme}
          applyMaxHeight={context.type !== "voting"}
          tealiumConfig={globals.tealiumConfig}
          onClose={toggleOutro.bind(this, outro.outroType)}
          onSubmit={globals.handleRegistration}
          onSubmitEnd={onSubmitEnd?.bind(this)}
          isPreview={globals.preview}
        />
      );
      break;

    case OutroType.Request:
      if (!globals.tealiumConfig) {
        return null;
      }

      outroType = (
        <Question
          outroType={OutroType.Request}
          genderRequest={outro.genderRequest}
          ageRequest={outro.ageRequest}
          incomeRequest={outro.incomeRequest}
          icons={globals.icons}
          tealiumConfig={globals.tealiumConfig}
          onClose={toggleOutro.bind(this, outro.outroType)}
        />
      );
      break;
    case OutroType.RequestOrAdvertising:
      //decision logic: https://as-wiki.axelspringer.de/pages/viewpage.action?pageId=241896322
      if (!globals.tealiumConfig) {
        return null;
      }
      if (globals.tealiumConfig.is_premium_user) {
        outroType = (
          <Question
            outroType={OutroType.Request}
            genderRequest={outro.genderRequest}
            ageRequest={outro.ageRequest}
            incomeRequest={outro.incomeRequest}
            icons={globals.icons}
            tealiumConfig={globals.tealiumConfig}
            onClose={toggleOutro.bind(this, outro.outroType)}
          />
        );
        break;
      } else {
        const randomizer = Math.floor(Math.random() * 100);
        if (randomizer < 61) {
          if (
            !outro.advertisingAdSlot ||
            !outro.advertisingWidth ||
            !outro.advertisingHeight
          ) {
            return null;
          }

          outroType = (
            <Advertisement
              adSlot={outro.advertisingAdSlot}
              height={Number(outro.advertisingHeight)}
              width={Number(outro.advertisingWidth)}
              isPreview={globals.preview}
            />
          );
          break;
        } else {
          outroType = (
            <Question
              outroType={OutroType.Request}
              genderRequest={outro.genderRequest}
              ageRequest={outro.ageRequest}
              incomeRequest={outro.incomeRequest}
              icons={globals.icons}
              tealiumConfig={globals.tealiumConfig}
              onClose={toggleOutro.bind(this, outro.outroType)}
            />
          );
          break;
        }
      }
    default:
      return null;
  }

  // This looks broken to me, because OutroSlide, OutroOverlay will not receive many props here.
  // e.g. title, withTopPadding are props of OutroOverlay but only set on the children (outroType).
  // That has the effect, that e.g. for Registration the title <h1>Teilnehmerregistrierung</h1> is not shown anymore.
  return <OutroOverlay {...outroProps}>{outroType}</OutroOverlay>;
}
